import { useEffect, useMemo, useState } from 'react';
import { Trigger } from '@/hooks/useTriggerStream';
import { greenForDevice } from './vizPalette';
import { recentOrLatest } from './vizWindow';

interface Props {
  triggers: Trigger[];
  dark: boolean;
}

// Radial 24-hour clock — each trigger is a ray at its hour-angle.
// Length encodes recency, color = stable per device.
export function RadialClock({ triggers, dark }: Props) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const size = 700;
  const cx = size / 2;
  const cy = size / 2;
  const rInner = 60;
  const rOuter = 300;

  // Prefer the last 24h; widen automatically when the network was quiet.
  const recent = useMemo(
    () => recentOrLatest(triggers, 24 * 3600 * 1000, 1, 400).items,
    [triggers]
  );

  const text = dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';
  const tick = dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.35)';

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto" style={{ maxHeight: '85vh' }}>
      {/* Hour ticks + labels */}
      {Array.from({ length: 24 }, (_, h) => {
        const angle = (h / 24) * Math.PI * 2 - Math.PI / 2;
        const x1 = cx + Math.cos(angle) * (rOuter + 6);
        const y1 = cy + Math.sin(angle) * (rOuter + 6);
        const x2 = cx + Math.cos(angle) * (rOuter + (h % 6 === 0 ? 18 : 10));
        const y2 = cy + Math.sin(angle) * (rOuter + (h % 6 === 0 ? 18 : 10));
        const lx = cx + Math.cos(angle) * (rOuter + 32);
        const ly = cy + Math.sin(angle) * (rOuter + 32);
        return (
          <g key={h}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={tick} strokeWidth={1} />
            {h % 3 === 0 && (
              <text
                x={lx}
                y={ly + 3}
                textAnchor="middle"
                fontSize={10}
                fontFamily="ui-monospace, monospace"
                fill={text}
              >
                {String(h).padStart(2, '0')}
              </text>
            )}
          </g>
        );
      })}

      {/* Inner circle */}
      <circle cx={cx} cy={cy} r={rInner} fill="none" stroke={tick} strokeWidth={0.5} />
      <circle cx={cx} cy={cy} r={rOuter} fill="none" stroke={tick} strokeWidth={0.5} />

      {/* Rays */}
      {recent.map((t) => {
        const d = new Date(t.created_at);
        const hour = d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
        const angle = (hour / 24) * Math.PI * 2 - Math.PI / 2;
        const ageHrs = (now - d.getTime()) / (3600 * 1000);
        const recency = Math.max(0.1, 1 - ageHrs / 24);
        const len = rInner + (rOuter - rInner) * recency;
        const x1 = cx + Math.cos(angle) * rInner;
        const y1 = cy + Math.sin(angle) * rInner;
        const x2 = cx + Math.cos(angle) * len;
        const y2 = cy + Math.sin(angle) * len;
        return (
          <line
            key={t.id}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={greenForDevice(t.device_id)}
            strokeWidth={1}
            opacity={0.3 + recency * 0.5}
            strokeLinecap="round"
          />
        );
      })}

      {/* Center label */}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fontSize={11}
        fontFamily="ui-monospace, monospace"
        fill={text}
      >
        24 HOURS
      </text>
      <text
        x={cx}
        y={cy + 14}
        textAnchor="middle"
        fontSize={20}
        fontFamily="ui-monospace, monospace"
        fontWeight={600}
        fill={dark ? '#fff' : '#000'}
      >
        {recent.length}
      </text>
    </svg>
  );
}
